import {Decimal} from 'decimal.js';

interface MarketFormulaWeights {
  initialOraclePrice: number;
  initialStablePlusLandToken: number;
}

interface MarketFormulaWeights {
  initialOraclePrice: number;
  initialStablePlusLandToken: number;
}

interface CollateralDebtPosition {
  id: number;
  owner: string;
  stableTokenAmount: number;
  landTokenAmount: number;
}

type Transaction = {
  landTokenAmount: number;
  stableTokenAmount: number;
  collateralLandTokenAmount: number;
  collateralStableTokenAmount: number;
};
const createTransaction = (
  landTokenAmount: number,
  stableTokenAmount: number,
  collateralLandTokenAmount: number,
  collateralStableTokenAmount: number
): Transaction => ({
  landTokenAmount,
  stableTokenAmount,
  collateralLandTokenAmount,
  collateralStableTokenAmount,
});

const IDECIMAL_MULTIPLIER = 1_000_000;
const SOLVER_DECIMALS_MULTIPLIER = 1 / IDECIMAL_MULTIPLIER;
const A_INPUT = 10 * IDECIMAL_MULTIPLIER;
const MAX_INT = 2 ** 256 - 1;
const LIQUIDATION_RATIO_PERCENT = 10000;
const DECIMAL_MULTIPLIER = 1_000_000;

const sellLandTokenTransaction = (
  landTokenAmount: number,
  availableTokens: number,
  liquidity: number,
  oraclePrice: number,
  initialWeights: MarketFormulaWeights,
  liquidationRatio: number,
  collateralDebtPosition: CollateralDebtPosition
): Transaction => {
  let landTokenAmountReceivedFromLiquidity = 0;
  let stableTokenAmountReceivedFromLiquidity = 0;
  let landTokenAmountReceivedFromCollateral = 0;
  let stableTokenAmountReceivedFromCollateral = 0;

  const mintCost = Math.floor(((liquidationRatio + 1) * oraclePrice) / LIQUIDATION_RATIO_PERCENT);

  const marketPriceResult =
    liquidity > 0 && availableTokens > 0 ? marketPrice(availableTokens, liquidity, initialWeights) : mintCost;

  const amountReceivedFromCollateralPerToken = mintCost > marketPriceResult ? marketPriceResult : mintCost;

  if (landTokenAmount > 0) {
    if (landTokenAmount <= collateralDebtPosition.landTokenAmount) {
      landTokenAmountReceivedFromCollateral = landTokenAmount;
    } else {
      landTokenAmountReceivedFromLiquidity = landTokenAmount - collateralDebtPosition.landTokenAmount;
      stableTokenAmountReceivedFromLiquidity = landTokenSellValueFromLiquidity(
        landTokenAmountReceivedFromLiquidity,
        availableTokens,
        liquidity,
        initialWeights
      );
      landTokenAmountReceivedFromCollateral = collateralDebtPosition.landTokenAmount;
    }
  }

  stableTokenAmountReceivedFromCollateral =
    (landTokenAmountReceivedFromCollateral * amountReceivedFromCollateralPerToken) / DECIMAL_MULTIPLIER;

  return createTransaction(
    landTokenAmountReceivedFromLiquidity,
    stableTokenAmountReceivedFromLiquidity,
    landTokenAmountReceivedFromCollateral,
    stableTokenAmountReceivedFromCollateral
  );
};

const sellStableTokenAmountTransaction = (
  stableTokenAmount: number,
  availableTokens: number,
  liquidity: number,
  oraclePrice: number,
  initialWeights: MarketFormulaWeights,
  liquidationRatio: number,
  collateralDebtPosition: CollateralDebtPosition
): Transaction => {
  let landTokenAmountReceivedFromLiquidity = 0;
  let stableTokenAmountReceivedFromLiquidity = 0;
  let landTokenAmountReceivedFromCollateral = 0;
  let stableTokenAmountReceivedFromCollateral = 0;

  const amountReceivedFromCollateralPerToken = Math.floor(
    ((liquidationRatio + 1) * oraclePrice) / LIQUIDATION_RATIO_PERCENT
  );

  if (stableTokenAmount > 0) {
    const landTokenAmount = (stableTokenAmount * DECIMAL_MULTIPLIER) / amountReceivedFromCollateralPerToken;

    if (landTokenAmount <= collateralDebtPosition.landTokenAmount) {
      landTokenAmountReceivedFromCollateral = landTokenAmount;
      stableTokenAmountReceivedFromCollateral =
        (landTokenAmountReceivedFromCollateral * amountReceivedFromCollateralPerToken) / DECIMAL_MULTIPLIER;
    } else {
      landTokenAmountReceivedFromCollateral = collateralDebtPosition.landTokenAmount;
      stableTokenAmountReceivedFromCollateral =
        (landTokenAmountReceivedFromCollateral * amountReceivedFromCollateralPerToken) / DECIMAL_MULTIPLIER;

      stableTokenAmountReceivedFromLiquidity = stableTokenAmount - stableTokenAmountReceivedFromCollateral;
      landTokenAmountReceivedFromLiquidity = landTokenAmountReceiveFromLiquidityForStableToken(
        stableTokenAmountReceivedFromLiquidity,
        availableTokens,
        liquidity,
        initialWeights
      );
    }
  }

  return createTransaction(
    landTokenAmountReceivedFromLiquidity,
    stableTokenAmountReceivedFromLiquidity,
    landTokenAmountReceivedFromCollateral,
    stableTokenAmountReceivedFromCollateral
  );
};

const purchaseLandTokenTransaction = (
  landTokenAmount: number,
  availableTokens: number,
  liquidity: number,
  oraclePrice: number,
  initialWeights: MarketFormulaWeights,
  liquidationRatio: number,
  liquidationLandRatio: number
): Transaction => {
  const [tokensPurchased, purchaseCost] = maximumPurchasableToken(
    landTokenAmount,
    availableTokens,
    liquidity,
    oraclePrice,
    initialWeights,
    liquidationRatio,
    liquidationLandRatio
  );
  const landTokenAmountToAddInCollateral = landTokenAmount - tokensPurchased;

  const mintCost = purchaseMintCost(availableTokens, liquidity, oraclePrice, liquidationRatio);

  const totalMintCost =
    landTokenAmountToAddInCollateral > 0
      ? Math.ceil((landTokenAmountToAddInCollateral * mintCost) / DECIMAL_MULTIPLIER)
      : 0;

  return createTransaction(tokensPurchased, purchaseCost, landTokenAmountToAddInCollateral, totalMintCost);
};

const purchaseStableTokenTransaction = (
  stableTokenAmount: number,
  availableTokens: number,
  liquidity: number,
  oraclePrice: number,
  initialWeights: MarketFormulaWeights,
  liquidationRatio: number,
  liquidationLandRatio: number
): Transaction => {
  const tokensToPurchase = landTokenAmountForStableToken(stableTokenAmount, availableTokens, liquidity, initialWeights);

  const [tokensPurchased, purchaseCost] = maximumPurchasableToken(
    tokensToPurchase,
    availableTokens,
    liquidity,
    oraclePrice,
    initialWeights,
    liquidationRatio,
    liquidationLandRatio
  );

  const mintCost = purchaseMintCost(availableTokens, liquidity, oraclePrice, liquidationRatio);

  const mintableTokens = Math.floor(((stableTokenAmount - purchaseCost) * DECIMAL_MULTIPLIER) / mintCost);

  const totalMintCost = Math.ceil((mintableTokens * mintCost) / DECIMAL_MULTIPLIER);

  return createTransaction(tokensPurchased, purchaseCost, mintableTokens, totalMintCost);
};

const landTokenSellValueFromLiquidity = (
  landTokenAmount: number,
  availableTokens: number,
  liquidity: number,
  initialWeights: MarketFormulaWeights
): number => {
  if (liquidity < 1 || availableTokens < 1) {
    return 0;
  }

  const newLiquidity = solveLiquidity(availableTokens + landTokenAmount, initialWeights);
  const sellValue = liquidity - newLiquidity;
  return sellValue;
};

const landTokenAmountReceiveFromLiquidityForStableToken = (
  stableTokenAmount: number,
  availableTokens: number,
  liquidity: number,
  initialWeights: MarketFormulaWeights
): number => {
  if (liquidity < 1 || availableTokens < 1) {
    return 0;
  }

  const newLandTokenAmount = solveNumberOfTokens(liquidity - stableTokenAmount, initialWeights);
  const landTokenAmount = newLandTokenAmount - availableTokens;
  return landTokenAmount;
};

const landTokenAmountForStableToken = (
  stableTokenAmount: number,
  availableTokens: number,
  liquidity: number,
  initialWeights: MarketFormulaWeights
): number => {
  if (liquidity < 1 || availableTokens < 1) {
    return 0;
  }

  const newLandTokenAmount = solveNumberOfTokens(stableTokenAmount + liquidity, initialWeights);
  const landTokenAmountReceived = availableTokens - newLandTokenAmount;
  return landTokenAmountReceived;
};

const maximumPurchasableToken = (
  maxLandTokenAmount: number,
  availableTokens: number,
  liquidity: number,
  oraclePrice: number,
  initialWeights: MarketFormulaWeights,
  liquidationRatio: number,
  liqudationTokenRatio: number
): [number, number] => {
  if (liquidity < 1 || availableTokens < 1) {
    return [0, 0];
  }
  let newRatio = (buyPrice(availableTokens, liquidity, initialWeights) * DECIMAL_MULTIPLIER) / oraclePrice / 100;
  if (newRatio >= liquidationRatio) {
    return [0, 0];
  }

  let initialLandTokenAmount = initialWeights.initialStablePlusLandToken / 2;
  let minimumLandTokenAmount = initialLandTokenAmount - initialLandTokenAmount * liqudationTokenRatio / 100 / 100;

  if(availableTokens <= minimumLandTokenAmount) {
    return [0, 0];
  }
  let maximumAmountPurchased = (availableTokens - minimumLandTokenAmount);

  maximumAmountPurchased = maximumAmountPurchased > maxLandTokenAmount ? maxLandTokenAmount : maximumAmountPurchased;

  let stableTokenAmount = stableTokenRequiredToPurchaseTokenFromLiquidity(
    maximumAmountPurchased,
    availableTokens,
    liquidity,
    initialWeights
  );
  return [maximumAmountPurchased, stableTokenAmount];
};

const maximumArbitrageableToken = (
  availableTokens: number,
  liquidity: number,
  oraclePrice: number,
  initialWeights: MarketFormulaWeights,
  liquidationRatio: number
): [number, number] => {
  if (liquidity < 1 || availableTokens < 1) {
    return [0, 0];
  }
  let newRatio = (buyPrice(availableTokens, liquidity, initialWeights) * DECIMAL_MULTIPLIER) / oraclePrice / 100;
  if (newRatio <= liquidationRatio) {
    return [0, 0];
  }

  // quickly find maximum
  let maximumArbitrageableTokenAmount = 1_000_000;
  let stableTokenAmount = landTokenSellValueFromLiquidity(
    maximumArbitrageableTokenAmount,
    availableTokens,
    liquidity,
    initialWeights
  );
  let newMarketPrice = marketPrice(
    availableTokens + maximumArbitrageableTokenAmount,
    liquidity - stableTokenAmount,
    initialWeights
  );

  newRatio = (newMarketPrice * DECIMAL_MULTIPLIER) / oraclePrice / 100;
  while (newRatio > liquidationRatio && maximumArbitrageableTokenAmount * 2 < availableTokens) {
    maximumArbitrageableTokenAmount = maximumArbitrageableTokenAmount * 2;
    stableTokenAmount = landTokenSellValueFromLiquidity(
      maximumArbitrageableTokenAmount,
      availableTokens,
      liquidity,
      initialWeights
    );
    newMarketPrice = marketPrice(
      availableTokens + maximumArbitrageableTokenAmount,
      liquidity - stableTokenAmount,
      initialWeights
    );
    newRatio = (newMarketPrice * DECIMAL_MULTIPLIER) / oraclePrice / 100;
  }

  // go back to find the exact amount
  let currentValidation = maximumArbitrageableTokenAmount;
  while (newRatio < liquidationRatio && (maximumArbitrageableTokenAmount * 90) / 100 != 0) {
    maximumArbitrageableTokenAmount = currentValidation;
    currentValidation = (maximumArbitrageableTokenAmount * 98) / 100;
    stableTokenAmount = landTokenSellValueFromLiquidity(currentValidation, availableTokens, liquidity, initialWeights);
    newMarketPrice = marketPrice(availableTokens + currentValidation, liquidity - stableTokenAmount, initialWeights);
    newRatio = (newMarketPrice * DECIMAL_MULTIPLIER) / oraclePrice / 100;
  }
  stableTokenAmount = landTokenSellValueFromLiquidity(
    maximumArbitrageableTokenAmount,
    availableTokens,
    liquidity,
    initialWeights
  );

  return [maximumArbitrageableTokenAmount, stableTokenAmount];
};

const stableTokenRequiredToPurchaseTokenFromLiquidity = (
  landTokenAmount: number,
  availableTokens: number,
  liquidity: number,
  initialWeights: MarketFormulaWeights
): number => {
  if (landTokenAmount >= availableTokens || liquidity < 1) {
    return MAX_INT;
  }

  const newLiquidity = solveLiquidity(availableTokens - landTokenAmount, initialWeights);
  const cost = newLiquidity > liquidity ? newLiquidity - liquidity : 0;
  return cost;
};

const buyPrice = (availableTokens: number, liquidity: number, initialWeights: MarketFormulaWeights): number => {
  const newAvailTokens = solveNumberOfTokens(liquidity + 1_000_000, initialWeights);
  if (newAvailTokens > availableTokens) {
    return 0;
  }

  const numberOfTokenSold = availableTokens - newAvailTokens;

  return Math.floor((1_000_000 * DECIMAL_MULTIPLIER) / numberOfTokenSold);
};

const marketPrice = (availableTokens: number, liquidity: number, initialWeights: MarketFormulaWeights): number => {
  if (availableTokens < 1 || liquidity < 1) {
    return MAX_INT;
  }
  return buyPrice(availableTokens, liquidity, initialWeights);
};

const purchaseMintCost = (
  availableTokens: number,
  liquidity: number,
  oraclePrice: number,
  liquidationRatio: number
): number => {
  const mintCost = Math.floor(((liquidationRatio + 1) * oraclePrice) / LIQUIDATION_RATIO_PERCENT);

  const hasLiquidity = liquidity > 0 && availableTokens > 0;
  const _marketPrice = hasLiquidity ? Math.floor((liquidity * DECIMAL_MULTIPLIER) / availableTokens) : mintCost;

  return mintCost < _marketPrice ? _marketPrice : mintCost;
};

const solveNumberOfTokens = (liquidity: number, initialWeights: MarketFormulaWeights): number => {
  const TWO = new Decimal(2);
  const FOUR = new Decimal(4);
  const EIGHT = new Decimal(8);

  const X = new Decimal(liquidity).mul(SOLVER_DECIMALS_MULTIPLIER);
  const P = new Decimal(initialWeights.initialOraclePrice).mul(SOLVER_DECIMALS_MULTIPLIER);
  const D = new Decimal(initialWeights.initialStablePlusLandToken).mul(SOLVER_DECIMALS_MULTIPLIER);
  const A = new Decimal(A_INPUT).mul(SOLVER_DECIMALS_MULTIPLIER);

  const FOURAX2 = FOUR.mul(A).mul(X.pow(TWO));
  const FOURAX = FOUR.mul(A).mul(X);
  const FOURAD2P2 = FOUR.mul(A).mul(D.pow(TWO)).mul(P.pow(TWO));
  const EIGHTADPX = EIGHT.mul(A).mul(D).mul(P).mul(X);
  const DP = D.mul(P);
  const DPX = DP.mul(X);

  const sqrt = X.mul(DP.add(FOURAX)).mul(FOURAX2.add(DPX).add(FOURAD2P2).sub(EIGHTADPX)).sqrt();

  const result = FOURAX2.sub(sqrt).add(DPX).sub(EIGHTADPX.div(TWO)).neg().div(EIGHTADPX.div(D));

  return Math.floor(result.div(SOLVER_DECIMALS_MULTIPLIER).toNumber());
};

const solveLiquidity = (liquidity: number, initialWeights: MarketFormulaWeights): number => {
  const TWO = new Decimal(2);
  const FOUR = new Decimal(4);
  const EIGHT = new Decimal(8);

  const Y = new Decimal(liquidity * SOLVER_DECIMALS_MULTIPLIER);
  const P = new Decimal(initialWeights.initialOraclePrice * SOLVER_DECIMALS_MULTIPLIER);
  const D = new Decimal(initialWeights.initialStablePlusLandToken * SOLVER_DECIMALS_MULTIPLIER);
  const A = new Decimal(A_INPUT * SOLVER_DECIMALS_MULTIPLIER);

  const FOURAY2 = FOUR.mul(A).mul(Y.pow(TWO));
  const FOURAD2 = FOUR.mul(A).mul(D.pow(TWO));

  const sqrt = Y.mul(D.plus(FOUR.mul(A).mul(Y)))
    .mul(FOURAD2.plus(FOURAY2).plus(D.mul(Y)).minus(EIGHT.mul(A).mul(D).mul(Y)))
    .sqrt();

  const result = P.mul(sqrt)
    .minus(D.mul(P).mul(Y))
    .minus(FOURAY2.mul(P))
    .plus(FOUR.mul(A).mul(D).mul(P).mul(Y))
    .div(EIGHT.mul(A).mul(Y));

  return Math.floor(result.div(SOLVER_DECIMALS_MULTIPLIER).toNumber());
};

export const MarketFormulaJS = {
  sellLandTokenTransaction,
  sellStableTokenAmountTransaction,
  purchaseLandTokenTransaction,
  purchaseStableTokenTransaction,
  landTokenAmountForStableToken,
  maximumPurchasableToken,
  maximumArbitrageableToken,
  buyPrice,
  marketPrice,
  purchaseMintCost,
  solveNumberOfTokens,
  solveLiquidity,
};
